import { Button, Input, Popconfirm, Table, Tag } from 'antd';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import useLoginStore from 'store/loginStore';
import useNotificationStore from 'store/notificationStore';
import { deleteOrganizationWebhook, getOrganizationWebhook, regenerateOrganizationWebhookSecret, setOrganizationWebhook, setOrganizationWebhookSecret, testOrganizationWebhook } from 'utils/apiFunctions';

const maskSecret = (secret) => '•'.repeat(20) + secret.slice(-6);

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    return false;
  }
}

const STATUS_COLORS = {
  SENT: 'green',
  PENDING: 'blue',
  PROCESSING: 'blue',
  ERROR: 'orange',
  FAILED: 'red',
  CANCELLED: 'default',
};

const formatDateTime = (value) => value ? dayjs(value).format('D. M. YYYY HH:mm:ss') : '';

// HMS-78: one webhook per organization - HMS POSTs game.finished / game.updated (signed by HMAC) to the URL
const OrganizationWebhook = ({ organizationId }) => {
  const currentRole = useLoginStore(s => s.currentRole);
  const addMessage = useNotificationStore((state) => state.addMessage);
  const [webhook, setWebhook] = useState(null);
  const [url, setUrl] = useState('');
  const [isSecretRevealed, setIsSecretRevealed] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [ownSecret, setOwnSecret] = useState(null);   // null = the own secret input is hidden

  const call = async (fn, ...args) => {
    setIsBusy(true);
    try {
      const response = await fn(organizationId, currentRole, ...args);
      const body = await response.json();
      if (response.status !== 200) {
        throw new Error(body?.message ?? 'Error');
      }
      return body;
    } catch (e) {
      addMessage({ text: e.message, type: 'error' });
      return null;
    } finally {
      setIsBusy(false);
    }
  }

  const apply = (body) => {
    if (body) {
      setWebhook(body);
      setUrl(body.webhookUrl ?? '');
    }
  }

  const load = async () => apply(await call(getOrganizationWebhook));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const save = async () => {
    const body = await call(setOrganizationWebhook, url.trim());
    if (body) {
      apply(body);
      addMessage({ text: 'Webhook saved', type: 'success' });
    }
  }

  const remove = async () => {
    if (await call(deleteOrganizationWebhook)) {
      setIsSecretRevealed(false);
      addMessage({ text: 'Webhook removed', type: 'success' });
      await load();
    }
  }

  const regenerateSecret = async () => {
    const body = await call(regenerateOrganizationWebhookSecret);
    if (body) {
      apply(body);
      setIsSecretRevealed(false);
      addMessage({ text: 'Webhook secret regenerated', type: 'success' });
    }
  }

  const saveOwnSecret = async () => {
    const body = await call(setOrganizationWebhookSecret, ownSecret.trim());
    if (body) {
      apply(body);
      setOwnSecret(null);
      setIsSecretRevealed(false);
      addMessage({ text: 'Webhook secret saved', type: 'success' });
    }
  }

  const test = async () => {
    const result = await call(testOrganizationWebhook);
    if (result) {
      addMessage(result.ok
        ? { text: `Test event delivered (HTTP ${result.status})`, type: 'success' }
        : { text: `Test event failed: ${result.error}`, type: 'error' });
    }
  }

  const isSet = !!webhook?.webhookUrl;
  const isChanged = url.trim() !== (webhook?.webhookUrl ?? '');

  const columns = [
    { title: 'Created', dataIndex: 'createdAt', render: formatDateTime },
    { title: 'Event', dataIndex: 'eventType' },
    { title: 'Game', dataIndex: 'gameId', render: (value) => <span className="font-mono text-xs">{value}</span> },
    { title: 'Status', dataIndex: 'status', render: (value) => <Tag className="mr-0" color={STATUS_COLORS[value]}>{value}</Tag> },
    { title: 'Attempts', dataIndex: 'attempts' },
    { title: 'Last attempt', dataIndex: 'lastAttemptAt', render: formatDateTime },
    { title: 'Response / error', render: (_, row) => row.lastError ?? (row.lastResponseStatus ? `HTTP ${row.lastResponseStatus}` : '') },
    { title: 'Next attempt', dataIndex: 'nextAttemptAt', render: (value, row) => row.status === 'ERROR' ? formatDateTime(value) : '' },
  ];

  return (
    <div className="flex flex-col gap-4 p-4 max-w-[1200px]">
      <div className="flex flex-col gap-1">
        <b>Webhook</b>
        <div className="text-gray-500 text-sm">
          HMS sends a <code>POST</code> request to this URL when a game is finished (<code>game.finished</code>) and when a finished game
          is corrected later (<code>game.updated</code>) - about 2-3 minutes after the last change of the game. Failed deliveries are retried
          (up to 10 attempts over ~8.5 hours, the waits double: 1, 2, 4 ... 256 minutes).
        </div>
      </div>

      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPressEnter={() => isChanged && url.trim() && save()}
          placeholder="https://your-server.example.com/hms-webhook"
          disabled={!webhook}
        />
        <Button type="primary" loading={isBusy} disabled={!isChanged || !url.trim()} onClick={save}>{isSet ? 'Save' : 'Set webhook'}</Button>
        {isSet && (
          <Popconfirm
            title="Remove webhook"
            description="HMS will stop sending events; undelivered events are cancelled."
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={remove}
          >
            <Button danger loading={isBusy}>Remove</Button>
          </Popconfirm>
        )}
      </div>

      {isSet && (
        <>
          <div className="flex flex-col gap-1">
            <div className="text-sm">Signing secret</div>
            <div className="flex gap-2">
              <Input
                value={webhook.secret ? (isSecretRevealed ? webhook.secret : maskSecret(webhook.secret)) : ''}
                placeholder="The secret cannot be displayed - regenerate it"
                readOnly
                className="font-mono"
              />
              {webhook.secret && <Button onClick={() => setIsSecretRevealed(r => !r)}>{isSecretRevealed ? 'Hide' : 'Show'}</Button>}
              {webhook.secret && <CopyToClipboardButton onClick={() => copyText(webhook.secret)} />}
              <Popconfirm
                title="Regenerate webhook secret"
                description="Requests will be signed by the new secret immediately."
                okText="Regenerate"
                okButtonProps={{ danger: true }}
                onConfirm={regenerateSecret}
              >
                <Button loading={isBusy}>Regenerate</Button>
              </Popconfirm>
              {ownSecret == null && <Button onClick={() => setOwnSecret('')}>Use own secret</Button>}
            </div>
            {ownSecret != null && (
              <div className="flex gap-2">
                <Input.Password
                  value={ownSecret}
                  onChange={(e) => setOwnSecret(e.target.value)}
                  onPressEnter={() => ownSecret.trim() && saveOwnSecret()}
                  placeholder="Secret agreed with the receiver (32-100 characters: letters, digits and . _ ~ + / = -)"
                  className="font-mono"
                  autoComplete="off"
                />
                <Button type="primary" loading={isBusy} disabled={!ownSecret.trim()} onClick={saveOwnSecret}>Save secret</Button>
                <Button onClick={() => setOwnSecret(null)}>Cancel</Button>
              </div>
            )}
            <div className="text-gray-500 text-sm">
              Every request carries the headers <code>X-HMS-Event</code>, <code>X-HMS-Event-Id</code>, <code>X-HMS-Timestamp</code> and <code>X-HMS-Signature: sha256=&lt;hex&gt;</code>{' '}
              (HMAC-SHA256 of <code>{'<X-HMS-Timestamp>.<raw request body>'}</code> with this secret) and <code>Authorization: Bearer &lt;secret&gt;</code>{' '}
              - the receiver verifies one of them.
            </div>
          </div>

          <div>
            <Button loading={isBusy} onClick={test}>Send test event</Button>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <b>Recent deliveries</b>
              <Button size="small" loading={isBusy} onClick={load}>Refresh</Button>
            </div>
            <Table
              size="small"
              rowKey="webhookDeliveryId"
              columns={columns}
              dataSource={webhook.deliveries ?? []}
              pagination={false}
              locale={{ emptyText: 'No events yet - they are created when a game is finished.' }}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default OrganizationWebhook
